import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  DepositBatchDetail,
  DepositBatchSummary,
  UpdateDepositBatchItemInput,
} from '@erp/rpc';

/** How often an open deposit re-reads while its files are still being read. */
const PROCESSING_POLL_MS = 3000;

export function useArDepositBatches() {
  const query = useQuery(
    orpc.accountsReceivable.arDepositBatches.list.queryOptions({ input: {} }),
  );
  return {
    ...query,
    batches: (query.data?.batches ?? []) as DepositBatchSummary[],
  };
}

export function useArDepositBatch(id: string | undefined) {
  const query = useQuery({
    ...orpc.accountsReceivable.arDepositBatches.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    // Files are read in the background; keep looking until they are done.
    refetchInterval: (latest) =>
      (latest.state.data?.batch.processingCount ?? 0) > 0
        ? PROCESSING_POLL_MS
        : false,
  });
  return {
    ...query,
    batch: (query.data?.batch ?? null) as DepositBatchDetail | null,
  };
}

/** Every mutation answers with the whole batch: drop it into the cache. */
function useStoreBatch() {
  const queryClient = useQueryClient();
  return async (batch: DepositBatchDetail) => {
    queryClient.setQueryData(
      orpc.accountsReceivable.arDepositBatches.get.queryKey({
        input: { id: batch.id },
      }),
      { batch },
    );
    await queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.arDepositBatches.list.key(),
    });
  };
}

export function useCreateArDepositBatch() {
  const store = useStoreBatch();
  return useMutation({
    mutationFn: async (input: { name?: string | null }) =>
      rpcClient.accountsReceivable.arDepositBatches.create(input),
    onSuccess: (data) => store(data.batch),
  });
}

export function useAddArDepositBatchDocuments() {
  const store = useStoreBatch();
  return useMutation({
    mutationFn: async (input: { id: string; documentIds: string[] }) =>
      rpcClient.accountsReceivable.arDepositBatches.addDocuments(input),
    onSuccess: (data) => store(data.batch),
  });
}

export function useUpdateArDepositBatchItem() {
  const store = useStoreBatch();
  return useMutation({
    mutationFn: async (input: UpdateDepositBatchItemInput) =>
      rpcClient.accountsReceivable.arDepositBatches.updateItem(input),
    onSuccess: (data) => store(data.batch),
  });
}

export function useRemoveArDepositBatchItem() {
  const store = useStoreBatch();
  return useMutation({
    mutationFn: async (input: { id: string; itemId: string }) =>
      rpcClient.accountsReceivable.arDepositBatches.removeItem(input),
    onSuccess: (data) => store(data.batch),
  });
}

export function useRetryArDepositBatch() {
  const store = useStoreBatch();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      rpcClient.accountsReceivable.arDepositBatches.retry(input),
    onSuccess: (data) => store(data.batch),
  });
}

export function useDeleteArDepositBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      rpcClient.accountsReceivable.arDepositBatches.delete(input),
    onSuccess: async (data) => {
      queryClient.removeQueries({
        queryKey: orpc.accountsReceivable.arDepositBatches.get.queryKey({
          input: { id: data.id },
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arDepositBatches.list.key(),
      });
    },
  });
}

export function useSubmitArDepositBatch() {
  const store = useStoreBatch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; depositDate: string }) =>
      rpcClient.accountsReceivable.arDepositBatches.submit(input),
    onSuccess: async (data) => {
      await store(data.batch);
      // The new cash receipts show up on the cash receipts page.
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.list.key(),
      });
    },
  });
}
